import React, { useState, useEffect, useId } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

export default function SmtpConfigTab() {
  const uid = useId();
  const { user } = useAuth();
  const { showToast } = useToast();
  const notify = (msg: string) =>
    showToast(msg.startsWith('❌') ? 'error' : 'success', msg.replace(/^[✅❌]\s*/, ''));

  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpSecure, setSmtpSecure] = useState(false);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [smtpFrom, setSmtpFrom] = useState('');
  const [smtpFromName, setSmtpFromName] = useState('Jarvis');
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [isSavingSmtp, setIsSavingSmtp] = useState(false);
  const [isTestingSmtp, setIsTestingSmtp] = useState(false);

  // Prefill SMTP config on mount (admin only)
  useEffect(() => {
    const loadSmtpConfig = async () => {
      try {
        const resp = await apiService.getSmtpConfig();
        if (resp.data?.success && resp.data?.data) {
          const d = resp.data.data;
          setSmtpHost(d.host || '');
          setSmtpPort(String(d.port || 587));
          setSmtpSecure(!!d.secure);
          setSmtpUser(d.username || '');
          setSmtpFrom(d.fromEmail || '');
          setSmtpFromName(d.fromName || 'Jarvis');
          setSmtpPass(''); // Never prefill password
        }
      } catch (e) {
        // ignore if not configured yet
      }
    };
    if ((user?.role === 'admin')) {
      loadSmtpConfig();
    }
  }, [user]);

  const handleSaveSmtpConfig = async () => {
    if (!(user?.role === 'admin')) return;
    if (!smtpHost || !smtpUser || !smtpPass || !smtpFrom) {
      notify('❌ Host, Username, Password and From email are required');
      return;
    }
    setIsSavingSmtp(true);
    try {
      const resp = await apiService.updateSmtpConfig({
        host: smtpHost,
        port: parseInt(smtpPort || '587', 10),
        secure: smtpSecure,
        username: smtpUser,
        password: smtpPass,
        fromEmail: smtpFrom,
        fromName: smtpFromName || 'Jarvis',
      });
      if (resp.data?.success) {
        notify('✅ SMTP configuration saved successfully');
        setSmtpPass(''); // Clear password field after save
      } else {
        notify(`❌ ${resp.data?.error || 'Failed to save SMTP config'}`);
      }
    } catch (e: any) {
      notify(`❌ ${e.response?.data?.error || e.message || 'Save failed'}`);
    } finally {
      setIsSavingSmtp(false);
    }
  };

  const handleTestSmtpConnection = async () => {
    if (!smtpHost || !smtpUser || !smtpPass) {
      notify('❌ Host, Username and Password are required for test');
      return;
    }
    setIsTestingSmtp(true);
    try {
      const resp = await apiService.testSmtpConnection({
        host: smtpHost,
        port: parseInt(smtpPort || '587', 10),
        secure: smtpSecure,
        username: smtpUser,
        password: smtpPass,
      });
      if (resp.data?.success) {
        notify('✅ Connection successful');
      } else {
        notify(`❌ ${resp.data?.error || 'Connection failed'}`);
      }
    } catch (e: any) {
      notify(`❌ ${e.response?.data?.error || e.message || 'Connection failed'}`);
    } finally {
      setIsTestingSmtp(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">SMTP / email configuration</h3>
        <p className="text-sm text-gray-600">Configure the outgoing mail server for notifications</p>
      </div>

      {!(user?.role === 'admin') ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">⚠️ Only administrators can configure SMTP settings.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Server */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Mail server</h4>
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="flex-1">
                  <label htmlFor={`${uid}-1`} className="block text-xs text-gray-700 mb-1">SMTP host</label>
                  <input
                    id={`${uid}-1`}
                    type="text"
                    placeholder="smtp.gmail.com"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div style={{ width: '90px' }}>
                  <label htmlFor={`${uid}-2`} className="block text-xs text-gray-700 mb-1">Port</label>
                  <input
                    id={`${uid}-2`}
                    type="number"
                    placeholder="587"
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="smtp-secure"
                  checked={smtpSecure}
                  onChange={(e) => setSmtpSecure(e.target.checked)}
                  className="h-3.5 w-3.5 text-blue-600 rounded"
                />
                <label htmlFor="smtp-secure" className="text-xs text-gray-700">
                  Use SSL/TLS (port 465) — leave unchecked for STARTTLS (port 587)
                </label>
              </div>
            </div>
          </div>

          {/* Credentials */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Credentials</h4>
            <div className="space-y-3">
              <div>
                <label htmlFor={`${uid}-3`} className="block text-xs text-gray-700 mb-1">Username / email</label>
                <input
                  id={`${uid}-3`}
                  type="text"
                  placeholder="your-email@gmail.com"
                  value={smtpUser}
                  onChange={(e) => setSmtpUser(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor={`${uid}-4`} className="block text-xs text-gray-700 mb-1">Password / app password</label>
                <div className="relative">
                  <input
                    id={`${uid}-4`}
                    type={showSmtpPass ? 'text' : 'password'}
                    placeholder="Enter password (required to save)"
                    value={smtpPass}
                    onChange={(e) => setSmtpPass(e.target.value)}
                    className="w-full px-3 py-2 pr-10 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSmtpPass(!showSmtpPass)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showSmtpPass ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                  </button>
                </div>
                {smtpHost.toLowerCase().includes('gmail') && (
                  <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1">
                    <p className="font-semibold">⚠️ Gmail requires an App Password — your normal Gmail password won't work.</p>
                    <ol className="list-decimal list-inside space-y-0.5 text-amber-700">
                      <li>Go to <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="underline font-medium">myaccount.google.com/security</a></li>
                      <li>Enable <strong>2-Step Verification</strong> (required)</li>
                      <li>Search <strong>"App passwords"</strong> → Select <em>Mail</em> → <em>Other</em> → name it <em>Jarvis</em></li>
                      <li>Click <strong>Generate</strong> — copy the 16-char code (remove spaces)</li>
                      <li>Paste that code here as the password</li>
                    </ol>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sender info */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Sender details</h4>
            <div className="space-y-3">
              <div>
                <label htmlFor={`${uid}-5`} className="block text-xs text-gray-700 mb-1">From email</label>
                <input
                  id={`${uid}-5`}
                  type="email"
                  placeholder="notifications@yourcompany.com"
                  value={smtpFrom}
                  onChange={(e) => setSmtpFrom(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label htmlFor={`${uid}-6`} className="block text-xs text-gray-700 mb-1">From name</label>
                <input
                  id={`${uid}-6`}
                  type="text"
                  placeholder="Jarvis"
                  value={smtpFromName}
                  onChange={(e) => setSmtpFromName(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col space-y-2 pt-1">
            <div className="flex space-x-3">
              <button
                onClick={handleSaveSmtpConfig}
                disabled={isSavingSmtp}
                className="px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isSavingSmtp ? 'Saving…' : 'Save Configuration'}
              </button>
              <button
                onClick={handleTestSmtpConnection}
                disabled={isTestingSmtp}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-xs rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                {isTestingSmtp ? 'Testing…' : 'Test Connection'}
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 space-y-1">
            <p><strong>Gmail:</strong> smtp.gmail.com · Port 587 · SSL off · <span className="font-semibold">use App Password, not your Gmail password</span></p>
            <p><strong>Outlook / Office 365:</strong> smtp.office365.com · Port 587 · SSL off</p>
            <p><strong>Custom SMTP:</strong> Ask your email provider for settings</p>
          </div>
        </div>
      )}
    </div>
  );
}
